'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { toast } from 'sonner';

import { createCheckoutSession } from '@/app/(public)/precos/actions';
import { AppLinkButton } from '@/components/mui/app-link-button';
import { authConfig } from '@/config/auth-config';
import { trackAnalyticsEvent } from '@/libs/analytics-events';

const BILLING_URL = '/configuracoes/faturamento';
const EXISTING_SUBSCRIPTION_MESSAGES = ['Você já possui Pro. Gerencie sua assinatura no Portal do cliente.'];

export function isExistingSubscriptionCheckoutError(error: unknown) {
  return error instanceof Error && EXISTING_SUBSCRIPTION_MESSAGES.includes(error.message);
}

function getSafeCheckoutErrorMessage(error: unknown) {
  if (!(error instanceof Error)) return 'Não foi possível iniciar o Checkout.';

  const safeMessages = new Set([
    'É necessário entrar na sua conta.',
    'Período de cobrança inválido.',
    'Um Checkout já está sendo preparado. Tente novamente em instantes.',
  ]);
  return safeMessages.has(error.message) ? error.message : 'Não foi possível iniciar o Checkout.';
}

export function CheckoutErrorDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      disablePortal
      aria-labelledby='checkout-error-dialog-title'
      aria-describedby='checkout-error-dialog-description'
    >
      <DialogTitle id='checkout-error-dialog-title'>Trial do Pro não disponível</DialogTitle>
      <DialogContent>
        <DialogContentText id='checkout-error-dialog-description'>
          O trial de 30 dias e a assinatura Pro só podem ser ativados uma vez. Como você já usou ou possui uma
          assinatura, não é possível ativá-los novamente.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button component={Link} href={BILLING_URL} variant='contained' autoFocus>
          Ir para faturamento
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AuthenticatedCheckoutButton({
  billingPeriod,
  fullWidth,
  size,
  variant,
  label,
}: {
  billingPeriod: 'monthly' | 'annual';
  fullWidth: boolean;
  size: ButtonProps['size'];
  variant: ButtonProps['variant'];
  label?: ReactNode;
}) {
  const [loading, setLoading] = useState(false);
  const [checkoutErrorOpen, setCheckoutErrorOpen] = useState(false);
  return (
    <>
      <Button
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={loading}
        onClick={async () => {
          setLoading(true);
          trackAnalyticsEvent('begin_checkout', {
            currency: 'BRL',
            items: [
              {
                item_id: `pro_${billingPeriod}`,
                item_name: `Plano Pro ${billingPeriod}`,
                item_category: 'subscription',
                quantity: 1,
              },
            ],
            checkout_type: 'subscription',
            billing_period: billingPeriod,
          });
          try {
            const result = await createCheckoutSession(billingPeriod);
            if (result.url) window.location.assign(result.url);
          } catch (error) {
            if (isExistingSubscriptionCheckoutError(error)) {
              setCheckoutErrorOpen(true);
            } else {
              toast.error(getSafeCheckoutErrorMessage(error));
            }
          } finally {
            setLoading(false);
          }
        }}
      >
        {loading ? 'Abrindo Checkout…' : (label ?? `Assinar ${billingPeriod === 'monthly' ? 'mensal' : 'anual'}`)}
      </Button>
      <CheckoutErrorDialog open={checkoutErrorOpen} onClose={() => setCheckoutErrorOpen(false)} />
    </>
  );
}

export function ProCheckoutButton({
  billingPeriod,
  authenticated,
  fullWidth = false,
  size = 'medium',
  variant = 'contained',
  label,
}: {
  billingPeriod: 'monthly' | 'annual';
  authenticated: boolean;
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  label?: ReactNode;
}) {
  if (!authenticated)
    return (
      <AppLinkButton
        href={authConfig.loginUrlForRedirect('/precos')}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
      >
        {label ?? 'Entrar para assinar'}
      </AppLinkButton>
    );
  return (
    <AuthenticatedCheckoutButton
      billingPeriod={billingPeriod}
      fullWidth={fullWidth}
      size={size}
      variant={variant}
      label={label}
    />
  );
}
